import { Children, createContext, useContext } from 'react';
import { useHref, useLocation } from 'react-router';
import { matchPathPrefix } from '@gonfalon/navigator/matchers';
import { useSlots } from '@gonfalon/react/useSlots';
import { Tab, TabList, TabPanel, Tabs } from '@launchpad-ui/components';
import clsx from 'clsx';

import { EnvironmentController } from '../internal/EnvironmentController';
import { useEnvironmentSelectorPosition } from '../internal/useEnvironmentSelectorPosition';
import { useMaxWidthAndCenteredLayoutContent } from '../internal/useMaxWidthAndCenteredLayoutContent';

import { ContentBlock } from './ProjectEntityContent';

import styles from './ProjectEntityTabs.module.css';

// Context to share environment selector state with child components
const EnvironmentSelectorContext = createContext<{
  showInside: boolean;
  showOutside: boolean;
}>({
  showInside: false,
  showOutside: false,
});

// Context that carries the prefix-matched selected tab key so ProjectEntityTabPanel
// can use the same value as its id. This ensures the panel stays visible even when
// the current URL is a child route of the tab (e.g. /runs/:runId).
const SelectedTabKeyContext = createContext<string | undefined>(undefined);

/**
 * Recursively collect `href` props from a React node tree.
 * Tabs may be wrapped in elements like TooltipTrigger > Focusable, so a
 * depth-first walk is needed to find the href on the actual Tab descendant.
 */
function collectTabHrefs(nodes: React.ReactNode): string[] {
  const hrefs: string[] = [];
  Children.forEach(nodes, (child) => {
    if (!child || typeof child !== 'object' || !('props' in child)) {
      return;
    }
    const props = (child as React.ReactElement).props as Record<string, unknown>;
    const href = props.href;
    if (typeof href === 'string' && href.startsWith('/')) {
      const hrefWithoutSearch = href.includes('?') ? href.split('?')[0] : href;
      hrefs.push(hrefWithoutSearch);
    } else if (props.children) {
      hrefs.push(...collectTabHrefs(props.children as React.ReactNode));
    }
  });
  return hrefs;
}

export function ProjectEntityTab({ children, ...props }: React.ComponentProps<typeof Tab>) {
  const hrefString = String(props.href ?? '');
  const hrefWithoutSearch = hrefString.includes('?') ? hrefString.split('?')[0] : hrefString;
  return (
    <Tab className={styles.tab} id={hrefWithoutSearch} key={`${props.href}`} href={props.href} {...props}>
      {children}
    </Tab>
  );
}

export function ProjectEntityTabsPanelHeader({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export function ProjectEntityTabPanel({ children }: { children: React.ReactNode }) {
  const { showInside } = useContext(EnvironmentSelectorContext);
  // Use the prefix-matched key from context so the panel id stays in sync with
  // selectedKey even when the current URL is a child route of the active tab.
  const selectedTabKey = useContext(SelectedTabKeyContext);
  const location = useLocation();
  const currentHref = useHref({ ...location, search: '' });
  const id = selectedTabKey ?? currentHref;

  const [slots, restChildren] = useSlots(children, {
    tabsPanelHeader: ProjectEntityTabsPanelHeader,
  });

  return (
    <TabPanel id={id} className={styles.tabPanel}>
      {slots.tabsPanelHeader}
      {showInside && <EnvironmentController />}
      {restChildren}
    </TabPanel>
  );
}

export function ProjectEntityTabs({
  children,
  ...props
}: React.ComponentProps<typeof Tabs> & { children: React.ReactNode }) {
  const environmentSelectorPosition = useEnvironmentSelectorPosition();

  const showOutside = environmentSelectorPosition === 'outside-tab-panel';
  const showInside = environmentSelectorPosition === 'inside-tab-panel';

  const [slots, restChildren] = useSlots(children, {
    tabPanel: ProjectEntityTabPanel,
  });

  const location = useLocation();
  const currentHref = useHref({ ...location, search: '' });

  // Collect tab hrefs from children and find the most specific (longest) one that
  // matches the current pathname. matchPath with end:false does proper segment-boundary
  // prefix matching (e.g. /run does not match /runs/123) so the correct tab stays
  // highlighted when the URL is a child route (e.g. /runs/:runId → Results tab).
  const tabHrefs = collectTabHrefs(restChildren);
  const selectedKey =
    tabHrefs.filter((href) => matchPathPrefix(href, location.pathname)).sort((a, b) => b.length - a.length)[0] ??
    currentHref;

  const isMaxWidthAndCenteredLayoutContent = useMaxWidthAndCenteredLayoutContent();

  return (
    <EnvironmentSelectorContext.Provider value={{ showInside, showOutside }}>
      <SelectedTabKeyContext.Provider value={selectedKey}>
        {showOutside && (
          <ContentBlock>
            <EnvironmentController />
          </ContentBlock>
        )}
        <Tabs selectedKey={selectedKey} className={styles.tabs} {...props}>
          <ContentBlock>
            <TabList
              className={clsx(styles.tabList, {
                [styles.tabListNoMargin]: showOutside,
                [styles.tabListFullWidthBorder]: isMaxWidthAndCenteredLayoutContent,
              })}
            >
              {restChildren}
            </TabList>
          </ContentBlock>
          {slots.tabPanel}
        </Tabs>
      </SelectedTabKeyContext.Provider>
    </EnvironmentSelectorContext.Provider>
  );
}
